import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArchive, FiPlus, FiFolder, FiImage, FiChevronRight } from 'react-icons/fi';
import { apiFetch } from '../utils/api';
import { showToast } from './NotificationTray';
import PageHero from './PageHero';

// Scan Archive: the top level holds only years; a year holds its fixed twelve
// months. Names here are never editable, folders you can name live inside a
// month (ScanArchiveMonth). Route: /archive and /archive/year/:yearId.
export default function ScanArchive() {
  const navigate = useNavigate();
  const { yearId } = useParams();
  const [years, setYears] = useState([]);
  const [yearDetail, setYearDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newYear, setNewYear] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (yearId) {
        const res = await apiFetch(`/api/archive/years/${yearId}`);
        if (res.ok) setYearDetail(await res.json());
        else if (res.status === 404) navigate('/archive');
      } else {
        setYearDetail(null);
        const res = await apiFetch('/api/archive/years');
        if (res.ok) setYears((await res.json()).years || []);
      }
    } finally {
      setLoading(false);
    }
  }, [yearId, navigate]);

  useEffect(() => { load(); }, [load]);

  const createYear = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await apiFetch('/api/archive/years', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: newYear }),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Could not add that year', 'error');
        return;
      }
      setShowCreate(false);
      setNewYear('');
      navigate(`/archive/year/${data.year.id}`);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="mobile-content-wrapper">
        <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  // ── Year detail: the fixed twelve months ──
  if (yearId && yearDetail) {
    const { year, months } = yearDetail;
    return (
      <div className="mobile-content-wrapper">
        <PageHero eyebrow="Scan Archive" title={year.name}>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/archive" className="hover:text-blue-600">Archive</Link>
            <FiChevronRight size={14} />
            <span className="text-gray-800 font-medium">{year.name}</span>
          </div>
        </PageHero>
        <p className="text-sm text-gray-500 mt-2">
          Months are fixed. Open a month to create folders and add scans.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
          {months.map((m) => (
            <Link
              key={m.id}
              to={`/archive/month/${m.id}`}
              className="group border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-800">{m.name}</span>
                <FiFolder className="text-gray-300 group-hover:text-blue-400" />
              </div>
              <div className="mt-2 text-xs text-gray-500 flex items-center gap-3">
                <span className="flex items-center gap-1"><FiFolder size={12} />{m.folder_count}</span>
                <span className="flex items-center gap-1"><FiImage size={12} />{m.photo_count}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // ── Top level: years only ──
  return (
    <div className="mobile-content-wrapper">
      <PageHero eyebrow="Scan Archive" title="Your years">
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
        >
          <FiPlus size={16} /> Add a year
        </button>
      </PageHero>
      <p className="text-sm text-gray-500 mt-2">
        The archive is organized by date: pick a year, drill into a month, and file your scans in folders there.
      </p>

      {years.length === 0 ? (
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <FiArchive size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="font-semibold text-gray-800 mb-2">Start your archive</h3>
          <p className="text-sm text-gray-500 mb-6">
            Add the first year you want to track. Every year automatically gets its twelve months.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600"
          >
            <FiPlus size={16} /> Add a year
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
          {years.map((y) => (
            <div key={y.id} className="group border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow relative">
              <Link to={`/archive/year/${y.id}`} className="block p-5">
                <div className="text-2xl font-semibold text-gray-800">{y.name}</div>
                <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                  <FiImage size={12} /> {y.photo_count} photo{y.photo_count === 1 ? '' : 's'}
                </div>
              </Link>

            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-800 mb-1">Add a year</h3>
            <p className="text-sm text-gray-500 mb-4">
              Years are the top of the archive and cannot be renamed. January through December are created automatically.
            </p>
            <form onSubmit={createYear}>
              <input
                type="text"
                inputMode="numeric"
                value={newYear}
                onChange={(e) => setNewYear(e.target.value)}
                placeholder="1987"
                autoFocus
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newYear.trim()}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
                >
                  {creating ? 'Adding…' : 'Add year'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
